// "Record payment" — for money already received (cash, Zelle, Cash App, check,
// a card run elsewhere, or a client who paid before the Hub). Applies it to an
// open invoice, a completed round, or the prior / pre-Hub balance.
import { useMemo, useState } from "react";
import { Modal } from "../Modal";
import {
  ApiError, PAYMENT_METHOD_LABEL, payments as paymentsApi,
  type PaymentAppliedTo, type PaymentMethod, type PaymentsOverview,
} from "../../lib/api";

const METHODS: PaymentMethod[] = ["cash_app", "zelle", "cash", "check", "card", "fanbasis", "other"];
const money = (c: number) => (c / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });
const today = () => new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD in local time

export default function RecordPaymentModal({
  clientId, overview, presetInvoiceId, onClose, onSaved,
}: {
  clientId: string;
  overview: Pick<PaymentsOverview, "open_invoices" | "rounds">;
  presetInvoiceId?: string;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const openInvoices = overview.open_invoices.filter((i) => i.status !== "void" && i.balance_cents > 0);
  const [appliedTo, setAppliedTo] = useState<PaymentAppliedTo>(presetInvoiceId || openInvoices.length ? "invoice" : "pre_hub");
  const [invoiceId, setInvoiceId] = useState(presetInvoiceId || openInvoices[0]?.id || "");
  const [roundId, setRoundId] = useState(overview.rounds[0]?.id || "");
  const invoice = openInvoices.find((i) => i.id === invoiceId);
  const [amount, setAmount] = useState(() => (invoice ? (invoice.balance_cents / 100).toFixed(2) : ""));
  const [paidAt, setPaidAt] = useState(today());
  const [method, setMethod] = useState<PaymentMethod>("cash_app");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [paidInFull, setPaidInFull] = useState(false);
  const [sendReceipt, setSendReceipt] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [earlyWarning, setEarlyWarning] = useState<string | null>(null);

  const amountCents = useMemo(() => Math.round(Number(amount.replace(/[$,\s]/g, "")) * 100), [amount]);
  const partial = appliedTo === "invoice" && invoice && amountCents > 0 && amountCents < invoice.balance_cents && !paidInFull;

  function pickInvoice(id: string) {
    setInvoiceId(id);
    const inv = openInvoices.find((i) => i.id === id);
    if (inv) setAmount((inv.balance_cents / 100).toFixed(2));
  }

  async function save(acknowledgeEarly = false) {
    setErr(null);
    if (!Number.isFinite(amountCents) || amountCents <= 0) return setErr("Enter the amount received.");
    if (!paidAt || paidAt > today()) return setErr("Enter the date the money was received (today or earlier).");
    if (appliedTo === "invoice" && !invoiceId) return setErr("Choose the invoice this payment is for.");
    if (appliedTo === "round" && !roundId) return setErr("Choose the dispute round this payment is for.");
    setBusy(true);
    try {
      const r = await paymentsApi.record(clientId, {
        paid_at: paidAt, amount_cents: amountCents, method, applied_to: appliedTo,
        invoice_id: appliedTo === "invoice" ? invoiceId : undefined,
        round_id: appliedTo === "round" ? roundId : undefined,
        reference: reference.trim() || undefined, note: note.trim() || undefined,
        mark_paid_in_full: appliedTo === "invoice" ? paidInFull : undefined,
        send_receipt: sendReceipt, acknowledge_early: acknowledgeEarly || undefined,
      });
      const bits = [`${money(amountCents)} recorded.`];
      if (r.invoice) bits.push(r.invoice.status === "paid" ? "Invoice marked Paid." : `Invoice balance: ${money(r.invoice.balance_cents)}.`);
      if (sendReceipt) bits.push(r.receipt_sent ? "Receipt emailed." : `Receipt not sent${r.receipt_error ? ` (${r.receipt_error})` : ""}.`);
      onSaved(bits.join(" "));
    } catch (e: any) {
      if (e instanceof ApiError && e.body?.error === "work_not_completed") {
        setEarlyWarning(e.body?.message || "The work this payment covers isn't completed yet.");
      } else {
        setErr(e?.body?.message || e?.message || "Couldn't record the payment.");
      }
    } finally { setBusy(false); }
  }

  return (
    <Modal open title="Record payment" onClose={onClose}
      footer={earlyWarning ? (
        <>
          <button type="button" className="btn-secondary" onClick={() => setEarlyWarning(null)} disabled={busy}>Go back</button>
          <button type="button" className="primary-button" onClick={() => save(true)} disabled={busy}>
            {busy ? "Saving…" : "Record anyway"}
          </button>
        </>
      ) : (
        <>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="primary-button" onClick={() => save(false)} disabled={busy} data-testid="record-payment-save">
            {busy ? "Saving…" : "Record payment"}
          </button>
        </>
      )}>
      {earlyWarning ? (
        <div className="banner-warning" role="alert">
          <strong>The work this payment covers isn't done yet.</strong>
          <p style={{ margin: "6px 0 0" }}>{earlyWarning}</p>
          <p className="small" style={{ margin: "6px 0 0" }}>
            Credit repair law (CROA) doesn't allow charging before the work is performed. Only record this if the money was
            already received for completed work. The override is kept in the audit log.
          </p>
        </div>
      ) : (
        <div className="stacked-form">
          <label className="form-field"><span>Apply to</span>
            <select value={appliedTo} onChange={(e) => setAppliedTo(e.target.value as PaymentAppliedTo)}>
              <option value="invoice" disabled={!openInvoices.length}>An open invoice{openInvoices.length ? "" : " (none open)"}</option>
              <option value="round" disabled={!overview.rounds.length}>A dispute round{overview.rounds.length ? "" : " (none yet)"}</option>
              <option value="pre_hub">Prior balance / paid before the Hub</option>
            </select>
          </label>
          {appliedTo === "invoice" && (
            <label className="form-field"><span>Invoice</span>
              <select value={invoiceId} onChange={(e) => pickInvoice(e.target.value)}>
                {openInvoices.map((i) => (
                  <option key={i.id} value={i.id}>
                    {(i.description || `Invoice ${i.id.slice(0, 8)}`).slice(0, 60)} — balance {money(i.balance_cents)}{i.work_completed ? "" : " (work not completed)"}
                  </option>
                ))}
              </select>
            </label>
          )}
          {appliedTo === "round" && (
            <label className="form-field"><span>Dispute round</span>
              <select value={roundId} onChange={(e) => setRoundId(e.target.value)}>
                {overview.rounds.map((r) => (
                  <option key={r.id} value={r.id}>Round {r.round_number}{r.mailed_at ? ` — mailed ${new Date(r.mailed_at).toLocaleDateString()}` : " — not mailed yet"}</option>
                ))}
              </select>
            </label>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <label className="form-field"><span>Amount received</span>
              <input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
            </label>
            <label className="form-field"><span>Date received</span>
              <input type="date" value={paidAt} max={today()} onChange={(e) => setPaidAt(e.target.value)} />
            </label>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <label className="form-field"><span>Method</span>
              <select value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
                {METHODS.map((m) => <option key={m} value={m}>{PAYMENT_METHOD_LABEL[m]}</option>)}
              </select>
            </label>
            <label className="form-field"><span>Reference # (optional)</span>
              <input value={reference} onChange={(e) => setReference(e.target.value)} maxLength={120} placeholder="Check #, Zelle confirmation…" />
            </label>
          </div>
          <label className="form-field"><span>Note (optional)</span>
            <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
          </label>
          {appliedTo === "invoice" && (
            <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input type="checkbox" checked={paidInFull} onChange={(e) => setPaidInFull(e.target.checked)} />
              <span>Mark as paid in full</span>
            </label>
          )}
          {partial && <p className="muted small" style={{ margin: 0 }}>Partial payment — {money(invoice!.balance_cents - amountCents)} will still show as owed.</p>}
          {paidInFull && invoice && amountCents < invoice.balance_cents && (
            <p className="muted small" style={{ margin: 0 }}>The remaining {money(invoice.balance_cents - amountCents)} will be written off. This is recorded in the audit log.</p>
          )}
          <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input type="checkbox" checked={sendReceipt} onChange={(e) => setSendReceipt(e.target.checked)} />
            <span>Email the client a receipt</span>
          </label>
          {err && <p className="error-text" role="alert" style={{ margin: 0 }}>{err}</p>}
        </div>
      )}
    </Modal>
  );
}
